import { makeAutoObservable, runInAction } from "mobx";
import i18n from "../i18n";

export type Theme = "light" | "dark" | "system";
export type Language = "pl" | "en" | "de" | "uk" | "ko";

export default class UIStore {
  _theme: Theme = "system";
  _language: Language = "pl";
  isBookingOpen: boolean = false;

  constructor() {
    makeAutoObservable(this);

    const savedTheme = localStorage.getItem("theme") as Theme | null;
    const savedLang = localStorage.getItem("language") as Language | null;
    
    this._theme = savedTheme || "system";
    this._language = savedLang || "pl";

    i18n.changeLanguage(this._language);
  }

  // This method is bound to the class instance
  private handleSystemThemeChange = () => {
    // EN: Re-apply theme only if the current theme is 'system'.
    // PL: Zastosuj motyw ponownie tylko wtedy, gdy bieżący motyw to 'system'.
    if (this._theme === "system") {
      this.applyTheme(this._theme);
    }
  };
  
  private applyTheme(selectedTheme: Theme) {
    const root = window.document.documentElement;
    const isDark =
    selectedTheme === "dark" ||
    (selectedTheme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    root.classList.remove(isDark ? "light" : "dark");
    root.classList.add(isDark ? "dark" : "light");
  }

  get theme(): Theme {
    return this._theme;
  }
  
  get language(): Language {
    return this._language;
  }
  
  init() {
    const savedTheme = (localStorage.getItem("theme") as Theme) || "system";
    const savedLanguage =
      (localStorage.getItem("language") as Language) || "pl";
    runInAction(() => {
      this._theme = savedTheme;
      this._language = savedLanguage;
    });
    this.applyTheme(savedTheme);

    // safe i18n call (test-safe)
    const safeI18n = i18n as any;
    
    if (safeI18n?.changeLanguage) {
      safeI18n.changeLanguage(savedLanguage);
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", this.handleSystemThemeChange);
  }
  
  setTheme = (theme: Theme) => {
    this._theme = theme;
    localStorage.setItem("theme", theme);
    this.applyTheme(theme);
  };

  setLanguage = (language: Language) => {
    this._language = language;
    localStorage.setItem("language", language);

    const safeI18n = i18n as any;
    safeI18n.changeLanguage?.(language); 
  };

  setIsBookingOpen = (isOpen: boolean) => {
    this.isBookingOpen = isOpen;
  };
}


